import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, useSpring } from "framer-motion";
import { Ship } from "lucide-react";
import { EASE } from "./Reveal";

const u = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1800&q=80`;

const STAGES = [
  { place: "Overseas yard", title: "Material is sourced", text: "We identify a trusted yard or recycler with the right grade and volume — and verify the material before any commitment.", img: u("photo-1708064235939-0b78938aa224") },
  { place: "Inspection", title: "Graded, packed & checked", text: "Grade, packing and paperwork are confirmed against the agreed specification, so what ships matches the offer.", img: u("photo-1516297702292-c919b6adea0c") },
  { place: "Port of loading", title: "Containers loaded", text: "We coordinate loading, documentation and the commercial flow through to shipment.", img: u("photo-1606185540834-d6e7483ee1a4") },
  { place: "At sea", title: "Cargo on the water", text: "The shipment crosses the ocean while we keep both sides updated, every step of the way.", img: u("photo-1697283896674-23afad9b0958") },
  { place: "Indian plant", title: "Delivered to industry", text: "The material reaches the buyer's port and plant — ready to be melted, cast and given a new life.", img: u("photo-1791356099225-82e4901ea804") },
];

const Frame = ({ s, i, active, progress }) => {
  const scale = useTransform(progress, [i / 5, (i + 1) / 5], [1.12, 1]);
  return (
    <motion.div style={{ scale }} className={`absolute inset-0 transition-opacity duration-700 ${active === i ? "opacity-100" : "opacity-0"}`}>
      <img src={s.img} alt={s.title} className="h-full w-full object-cover" loading="lazy" />
    </motion.div>
  );
};

const Route = ({ progress, active }) => {
  const fill = useSpring(progress, { stiffness: 120, damping: 25 });
  const left = useTransform(fill, (v) => `${Math.min(100, Math.max(0, v * 100))}%`);
  return (
    <div className="relative" data-testid="journey-route">
      <div className="relative h-px bg-white/25">
        <motion.div style={{ scaleX: fill }} className="absolute inset-0 origin-left bg-gold-light" />
        <motion.div style={{ left }} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-gold-light text-navy shadow-[0_0_0_6px_rgba(217,181,86,0.25)]"><Ship className="h-4 w-4" /></span>
        </motion.div>
      </div>
      <div className="mt-6 grid grid-cols-5">
        {STAGES.map((s, i) => (
          <p key={s.place} className={`font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-500 sm:text-[11px] sm:tracking-[0.2em] ${i === 0 ? "" : i === 4 ? "text-right" : "text-center"} ${i <= active ? "text-gold-light" : "text-slate-400"}`}>
            <span className="hidden sm:inline">{s.place}</span><span className="sm:hidden">0{i + 1}</span>
          </p>
        ))}
      </div>
    </div>
  );
};

export const Journey = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => setActive(Math.min(4, Math.max(0, Math.floor(v * 5)))));
  const s = STAGES[active];

  return (
    <section ref={ref} id="journey" className="relative h-[460vh] bg-navy-deep" data-testid="journey-section">
      <div className="sticky top-0 h-[100svh] overflow-hidden text-white">
        {STAGES.map((st, i) => <Frame key={st.place} s={st} i={i} active={active} progress={scrollYProgress} />)}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-deep via-navy-deep/55 to-navy-deep/30" />
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-navy-deep/85 to-transparent" />
        <div className="relative mx-auto flex h-full max-w-[1400px] flex-col justify-between px-5 pb-10 pt-[132px] sm:px-8 sm:pb-14">
          <div className="flex items-start justify-between gap-6">
            <p className="flex items-center gap-3 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] text-gold-light sm:text-xs sm:tracking-[0.28em]">
              <span className="h-px w-8 bg-gold-light" />Journey of a shipment
            </p>
            <p className="whitespace-nowrap font-mono text-xs tracking-[0.2em] text-slate-300" data-testid="journey-counter">0{active + 1} / 05</p>
          </div>
          <div>
            <motion.div key={active} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="max-w-3xl" data-testid={`journey-stage-${active}`}>
              <p className="font-serif text-2xl italic text-gold-light sm:text-3xl">{s.place}</p>
              <h2 className="mt-3 font-display text-4xl font-extrabold leading-[1] tracking-tight sm:text-7xl">{s.title}</h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-200 md:text-lg">{s.text}</p>
            </motion.div>
            <div className="mt-12 pr-16 sm:mt-16 sm:pr-24"><Route progress={scrollYProgress} active={active} /></div>
          </div>
        </div>
      </div>
    </section>
  );
};
